import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Percent, Plus, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function DiscountsPage() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ student_id: "", payment_plan_id: "", percentage: 10, reason: "", applies_to: "tuition_only" });
  const [saving, setSaving] = useState(false);
  const [approveDialog, setApproveDialog] = useState(null);
  const [approveForm, setApproveForm] = useState({ approved: true, approved_percentage: 0, founder_note: "" });

  const load = async () => {
    const [r, s, p, u] = await Promise.allSettled([
      api.get("/scolarite/discounts"),
      api.get("/scolarite/students-search"),
      api.get("/scolarite/payment-plans"),
      api.get("/auth/me"),
    ]);
    if (r.status === "fulfilled") setRows(r.value.data);
    else toast.error("Impossible de charger les demandes de remise");
    if (s.status === "fulfilled") setStudents(s.value.data);
    if (p.status === "fulfilled") setPlans(p.value.data);
    if (u.status === "fulfilled") setMe(u.value.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.student_id || !form.reason.trim()) return toast.error("Élève + motif requis");
    setSaving(true);
    try {
      await api.post("/scolarite/discounts/request", {
        ...form, percentage: parseFloat(form.percentage),
        payment_plan_id: form.payment_plan_id || undefined,
      });
      toast.success("Demande envoyée au Fondateur");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    if (!approveDialog) return;
    setSaving(true);
    try {
      await api.post(`/scolarite/discounts/${approveDialog.id}/approve`, {
        approved: approveForm.approved,
        approved_percentage: approveForm.approved_percentage ? parseFloat(approveForm.approved_percentage) : undefined,
        founder_note: approveForm.founder_note,
      });
      toast.success(approveForm.approved ? "Approuvée" : "Rejetée");
      setApproveDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const STATUS_UI = {
    pending: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: Clock },
    approved: { label: "Approuvée", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
    rejected: { label: "Rejetée", color: "bg-red-100 text-red-800", icon: XCircle },
  };

  const isFounder = me?.role === "founder" || me?.role === "super_admin";
  const canRequest = me?.role === "scolarite" || me?.role === "admin";

  return (
    <div className="space-y-6" data-testid="discounts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><Percent className="h-3 w-3" /><span>REMISES</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Remises de frais</h1>
          <p className="text-slate-500 mt-1">Demandes de remise soumises par la Scolarité, validées par le Fondateur (pourcentage ajustable).</p>
        </div>
        {canRequest && (
          <Button onClick={() => setOpen(true)} className="bg-[#EA580C] hover:bg-orange-700 text-white gap-2" data-testid="new-discount-btn">
            <Plus className="h-4 w-4" />Nouvelle demande
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Élève</th>
                <th className="px-4 py-3 text-left">Classe</th>
                <th className="px-4 py-3 text-right">Demandé</th>
                <th className="px-4 py-3 text-right">Approuvé</th>
                <th className="px-4 py-3 text-left">Motif</th>
                <th className="px-4 py-3 text-center">Statut</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const st = STATUS_UI[r.status] || STATUS_UI.pending;
                const Ico = st.icon;
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`disc-row-${r.id}`}>
                    <td className="px-4 py-2 font-medium">{r.student_name}</td>
                    <td className="px-4 py-2">{r.class_name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.percentage}%</td>
                    <td className="px-4 py-2 text-right tabular-nums font-bold text-[#166534]">{r.approved_percentage || "—"}{r.approved_percentage ? "%" : ""}</td>
                    <td className="px-4 py-2 max-w-xs truncate text-xs">{r.reason}</td>
                    <td className="px-4 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-semibold ${st.color}`}><Ico className="h-3 w-3" />{st.label}</span></td>
                    <td className="px-4 py-2 text-right">
                      {isFounder && r.status === "pending" && (
                        <Button size="sm" onClick={() => { setApproveDialog(r); setApproveForm({ approved: true, approved_percentage: r.percentage, founder_note: "" }); }} className="bg-[#1E3A8A] text-white" data-testid={`decide-${r.id}`}>Décider</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Aucune demande</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* New request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle demande de remise</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Élève *</Label>
              <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                <SelectTrigger data-testid="dr-student"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.class_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan de paiement (optionnel)</Label>
              <Select value={form.payment_plan_id || "__none"} onValueChange={(v) => setForm({ ...form, payment_plan_id: v === "__none" ? "" : v })}>
                <SelectTrigger data-testid="dr-plan"><SelectValue placeholder="Sans plan lié" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Sans plan lié</SelectItem>
                  {plans.filter(p => p.student_id === form.student_id).map(p => <SelectItem key={p.id} value={p.id}>{p.plan_type} — {p.academic_year}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Pourcentage demandé *</Label>
                <Input type="number" step="1" min="0" max="100" value={form.percentage} onChange={e => setForm({ ...form, percentage: e.target.value })} data-testid="dr-pct" />
              </div>
              <div>
                <Label>Applique à</Label>
                <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                  <SelectTrigger data-testid="dr-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tuition_only">Scolarité uniquement</SelectItem>
                    <SelectItem value="registration_only">Inscription uniquement</SelectItem>
                    <SelectItem value="all_fees">Tous frais</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Motif *</Label>
              <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={3} placeholder="Ex : Fratrie 3+ enfants, situation sociale, mérite scolaire..." data-testid="dr-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#1E3A8A] text-white" data-testid="dr-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer au Fondateur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve dialog (Founder) */}
      <Dialog open={!!approveDialog} onOpenChange={(o) => !o && setApproveDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Décision — {approveDialog?.student_name}</DialogTitle></DialogHeader>
          {approveDialog && (
            <div className="space-y-3">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <div><strong>Demande :</strong> {approveDialog.percentage}% sur {approveDialog.applies_to}</div>
                <div className="mt-1"><strong>Motif :</strong> {approveDialog.reason}</div>
              </div>
              <div>
                <Label>Décision</Label>
                <Select value={approveForm.approved ? "yes" : "no"} onValueChange={(v) => setApproveForm({ ...approveForm, approved: v === "yes" })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Approuver</SelectItem>
                    <SelectItem value="no">Rejeter</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {approveForm.approved && (
                <div>
                  <Label>Pourcentage final (ajustable)</Label>
                  <Input type="number" step="1" min="0" max="100" value={approveForm.approved_percentage} onChange={e => setApproveForm({ ...approveForm, approved_percentage: e.target.value })} data-testid="ap-pct" />
                </div>
              )}
              <div>
                <Label>Note</Label>
                <Textarea value={approveForm.founder_note} onChange={e => setApproveForm({ ...approveForm, founder_note: e.target.value })} rows={2} data-testid="ap-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(null)}>Annuler</Button>
            <Button onClick={approve} disabled={saving} className="bg-[#166534] text-white" data-testid="ap-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
